import React, { createContext, useContext, useState } from 'react';
import type { KioskV2Session } from '../../api/bookingApi';

interface RetainedKioskSession {
  clientId: string;
  session: KioskV2Session;
  hasSubmittedForms: boolean;
}

interface KioskV2SessionContextValue {
  retained: RetainedKioskSession | null;
  retain: (clientId: string, session: KioskV2Session) => void;
  markSubmitted: (clientId: string, kioskToken: string) => void;
  clear: () => void;
}

const KioskV2SessionContext = createContext<KioskV2SessionContextValue | null>(null);

/**
 * Keeps the short-lived kiosk bearer in memory while the SPA moves between the
 * kiosk menu and a V2 form. Refreshing or closing the tab clears it; storage is
 * deliberately reserved for the PHI-free return hint.
 */
export function KioskV2SessionProvider({ children }: { children: React.ReactNode }) {
  const [retained, setRetained] = useState<RetainedKioskSession | null>(null);

  return (
    <KioskV2SessionContext.Provider
      value={{
        retained,
        retain: (clientId, session) => setRetained({ clientId, session, hasSubmittedForms: false }),
        markSubmitted: (clientId, kioskToken) => setRetained((current) => (
          current?.clientId === clientId && current.session.kiosk_token === kioskToken
            ? { ...current, hasSubmittedForms: true }
            : current
        )),
        clear: () => setRetained(null),
      }}
    >
      {children}
    </KioskV2SessionContext.Provider>
  );
}

export function useKioskV2Session(): KioskV2SessionContextValue {
  const value = useContext(KioskV2SessionContext);
  if (!value) throw new Error('KioskV2SessionProvider is missing');
  return value;
}
